import {
  defaultState as mfaPromoteDefaultState,
  State as MfaPromoteState,
} from '../reducers/mfa-promote-reducer';
import { State as Microbranding } from '../reducers/microbranding-reducer';
import {
  AppConfig,
  JWT,
  LoginFieldError,
  LoginGlobalError,
  MfaAllowedMethod,
  OidcContext,
} from '../types';
import {
  defaultState as awsCaptchaDefaultState,
  State as AwsCaptchaState,
} from '../reducers/aws-captcha-reducer';
import { fetchAwsWafConfig } from '../aws-captcha/fetch-aws-waf-config';
import { screenSize } from './style-utils';
import { State as UserState } from '../reducers/user-reducer';
import { defaultState, State as LoginState } from '../reducers/login-reducer';
import { State as LogoutState } from '../reducers/logout-reducer';
import { State as ContinueAsState } from '../reducers/continue-as-reducer';
import { State as CancelDeletionState } from '../reducers/cancel-deletion-reducer';
import { State as SelectAccountState } from '../reducers/select-account-reducer';
import { isMobileOidc } from './oidc/oidc';
import { State as ChangeEmailState } from '../reducers/change-email-reducer';
import {
  defaultState as changePasswordDefaultState,
  State as ChangePasswordState,
} from '../reducers/change-password-reducer';
import { State as CreateAccountState } from '../reducers/create-account-reducer';
import { State as ContextState } from '../reducers/context-reducer';
import { State as MigrateConfirmationState } from '../reducers/migrate-confirmation-reducer';
import { State as ServerErrorState } from '../reducers/server-error-reducer';
import {
  defaultState as signupDefaultState,
  State as SignupState,
} from '../reducers/signup-reducer';
import {
  defaultState as welcomeDefaultState,
  State as WelcomeState,
} from '../reducers/welcome-reducer';
import {
  State as VerifyEmailOtpState,
  defaultState as VerifyEmailOtpDefaultState,
} from '../reducers/verify-email-otp-reducer';
import { State as SocialLoginRecoveryState } from '../reducers/social-login-recovery-reducer';
import { State as VerifyEmailState } from '../reducers/verify-email-reducer';
import { State as WelcomeSentState } from '../reducers/welcome-sent-reducer';
import { State as VerifyEmailSentState } from '../reducers/verify-email-sent-reducer';
import { State as CobrandingState, StaticCobranding } from '../reducers/cobranding-reducer';
import { State as LocaleState } from '../reducers/locale-reducer';

import { AnalyticsClient, AnalyticsClientImpl } from './analytics/analytics-web-client';
import { State as MultiFactorState } from '../reducers/multi-factor-reducer';
import { defaultState as multiFactorDefaultState } from '../reducers/multi-factor-reducer';
import { State as MfaEnrollmentState } from '../reducers/mfa-enrollment-reducer';
import { defaultState as mfaEnrolmentDefaultState } from '../reducers/mfa-enrollment-reducer';
import { getIdacOrigin } from './env';
import {
  addBreadcrumb,
  captureTaggedException,
  setAppStateInitializationContext,
  AppStateInitializationTag,
} from './analytics/error-reporting';
import { State as StepUp } from '../reducers/step-up-reducer';
import { State as StepUpSso } from '../reducers/step-up-sso-reducer';
import { State as OrgLoginConfigState } from '../reducers/org-login-config-reducer';
import {
  State as LoginConsentState,
  defaultState as LoginConsentDefaultState,
} from '../reducers/login-consent-reducer';

/**
 * This is what comes as a json string from server
 */
export interface PreloadedAppState {
  csrfToken: string;
  hashedCsrfToken: string;
  appConfig: AppConfig;
  locale: string;
  tenantCloudId?: string;
  userId?: string;
  oidcContext?: OidcContext;
  user?: { email: string };
  continueAs?: ContinueAsState;
  cancelDeletion?: CancelDeletionState;
  selectAccount?: SelectAccountState;
  login?: {
    error?: LoginGlobalError;
    fieldErrors: LoginFieldError;
    recoveryToken?: JWT;
  };
  logout?: LogoutState;
  createAccount?: CreateAccountState;
  migrateConfirmation?: MigrateConfirmationState;
  signup?: {
    autoSubmit?: boolean;
  };
  welcome?: WelcomeState;
  socialLoginRecovery?: SocialLoginRecoveryState;
  verifyEmail?: VerifyEmailState;
  verifyEmailSent?: VerifyEmailSentState;
  welcomeSent?: WelcomeSentState;
  error?: ServerErrorState;
  changeEmail?: ChangeEmailState;
  changePassword?: ChangePasswordState;
  cobranding: CobrandingState;
  multiFactor?: MultiFactorState;
  stepUp?: StepUp;
  stepUpSso?: StepUpSso;
  cspNonce: string;
  verifyEmailOtp?: VerifyEmailOtpState;
  loginConsent?: LoginConsentState;
  orgLoginConfig?: OrgLoginConfigState;
  enrollment?: MfaEnrollmentState;
  allowedMethods?: MfaAllowedMethod[];
  mfaPromote?: MfaPromoteState;
}

interface FetchedAppState {
  appConfig: AppConfig;
  locale: string;
  csrfToken: string;
  cobranding: StaticCobranding;
  tenantCloudId: string;
  oidcContext: OidcContext;
  cspNonce: string;
}

type FetchAppStateResult =
  | { status: 'success'; data: FetchedAppState }
  | { status: 'client_error'; statusCode: number; message: string }
  | { status: 'server_error'; statusCode: number; message: string }
  | { status: 'network_error'; message: string }
  | { status: 'parse_error'; message: string };

/**
 * This is the state with which we hydrate redux store on application start
 */
export interface InitialStoreState {
  microbranding: Microbranding;
  csrfToken?: string;
  hashedCsrfToken?: string;
  appConfig?: AppConfig;
  awsCaptcha?: AwsCaptchaState;
  locale?: LocaleState;
  user?: UserState;
  login?: LoginState;
  logout?: LogoutState;
  continueAs?: ContinueAsState;
  cancelDeletion?: CancelDeletionState;
  selectAccount?: SelectAccountState;
  createAccount?: CreateAccountState;
  context: ContextState;
  migrateConfirmation?: MigrateConfirmationState;
  signup?: SignupState;
  welcome?: WelcomeState;
  socialLoginRecovery?: SocialLoginRecoveryState;
  verifyEmail?: VerifyEmailState;
  verifyEmailSent?: VerifyEmailSentState;
  welcomeSent?: WelcomeSentState;
  serverError?: ServerErrorState;
  changeEmail?: ChangeEmailState;
  changePassword?: ChangePasswordState;
  cobranding?: CobrandingState;
  multiFactor?: MultiFactorState;
  verifyEmailOtp?: VerifyEmailOtpState;
  stepUp?: StepUp;
  stepUpSso?: StepUpSso;
  loginConsent?: LoginConsentState;
  orgLoginConfig?: OrgLoginConfigState;
  mfaEnrollment?: MfaEnrollmentState;
  mfaPromote?: MfaPromoteState;
}

export interface InitialState {
  initialStoreState: InitialStoreState;
  cspNonce: string;
}

/**
 * Reads data-app-state attribute from <body> tag
 * Note that this will be available only if we render frontend from one of our services, e.g. not mfa page
 */
export function preloadAppState(): PreloadedAppState | null {
  try {
    const el = document.body;
    return JSON.parse((el && el.getAttribute('data-app-state')) || 'null');
  } catch (e) {
    return null;
  }
}

async function attemptFetchAppState(): Promise<FetchAppStateResult> {
  try {
    const res = await fetch(`${getIdacOrigin()}/frontend/state`, {
      mode: 'cors',
      credentials: 'include',
    });

    if (!res.ok) {
      const statusCode = res.status;
      const statusText = res.statusText;

      setAppStateInitializationContext({
        fetchResult: 'failed',
        statusCode,
        statusText,
      });

      addBreadcrumb({
        message: `AppState: fetch failed with status ${statusCode}`,
        data: { statusCode, statusText },
      });

      // client errors - don't retry
      if (statusCode >= 400 && statusCode < 500) {
        captureTaggedException(
          new Error(`Client error fetching app state: ${statusCode} ${statusText}`),
          {
            capabilities: [],
            tags: {
              appStateInitialization: 'app_state_client_error',
            },
            extraProperties: {
              status_code: statusCode.toString(),
            },
          }
        );
        return {
          status: 'client_error',
          statusCode,
          message: `Client error fetching app state: ${statusCode} ${statusText}`,
        };
      }

      // 5xx server errors - retryable
      return {
        status: 'server_error',
        statusCode,
        message: `Server error: ${statusCode} ${statusText}`,
      };
    }

    try {
      const data = await res.json();
      addBreadcrumb({
        message: 'AppState: fetch succeeded',
      });
      setAppStateInitializationContext({ fetchResult: 'succeeded' });
      return { status: 'success', data };
    } catch (parseError) {
      // JSON parsing failed - retryable
      const message = parseError instanceof Error ? parseError.message : 'Unknown parse error';
      addBreadcrumb({
        message: `AppState: parse error - ${message}`,
        level: 'error',
      });
      return {
        status: 'parse_error',
        message: `Failed to parse app state response: ${message}`,
      };
    }
  } catch (e) {
    // Network errors (DNS, connection, CORS, timeout, etc.) - retryable
    const errorMessage = e instanceof Error ? e.message : 'Unknown error';
    addBreadcrumb({
      message: `AppState: network error - ${errorMessage}`,
      level: 'error',
    });
    setAppStateInitializationContext({
      fetchResult: 'network_error',
      errorMessage,
    });
    return {
      status: 'network_error',
      message: `Network error: ${errorMessage}`,
    };
  }
}

type FetchAppStateWithRetryResult = FetchAppStateResult & {
  attempts?: number;
};

async function fetchAppStateWithRetry(
  attempt: number,
  maxRetries: number,
  delay: (attemptNumber: number) => Promise<void>
): Promise<FetchAppStateWithRetryResult> {
  addBreadcrumb({
    message: `AppState: fetch attempt ${attempt}/${maxRetries}`,
  });

  const result = await attemptFetchAppState();

  // Success - return immediately
  if (result.status === 'success') {
    setAppStateInitializationContext({ fetchResult: 'succeeded', attempt });
    return result;
  }

  // Non-retryable errors - return immediately
  if (result.status === 'client_error') {
    return result;
  }

  // Retryable errors - recurse if attempts remain
  if (attempt < maxRetries) {
    await delay(attempt);
    return fetchAppStateWithRetry(attempt + 1, maxRetries, delay);
  }

  // All retries exhausted - log and return final error with attempt count
  const appStateTag: AppStateInitializationTag = (() => {
    switch (result.status) {
      case 'server_error':
        return 'app_state_server_error';
      case 'network_error':
        return 'app_state_network_error';
      case 'parse_error':
        return 'app_state_parse_error';
      default:
        // Fallback - shouldn't reach here
        return 'app_state_server_error';
    }
  })();

  captureTaggedException(new Error(result.message), {
    capabilities: [],
    tags: {
      appStateInitialization: appStateTag,
    },
    extraProperties: {
      retry_attempts: maxRetries.toString(),
      status_code: 'statusCode' in result ? result.statusCode.toString() : undefined,
    },
  });

  return { ...result, attempts: maxRetries };
}

// Default delay function with exponential backoff
export const createDefaultDelay = (baseDelayMs: number = 1000) => {
  return async (attemptNumber: number) => {
    const delayMs = baseDelayMs * Math.pow(2, attemptNumber - 1);
    addBreadcrumb({
      message: `AppState: retrying after ${delayMs}ms`,
      data: { delayMs, attemptNumber },
    });
    await new Promise(resolve => setTimeout(resolve, delayMs));
  };
};

export async function fetchAppState(
  options: {
    maxRetries?: number;
    delay?: (attemptNumber: number) => Promise<void>;
  } = {}
): Promise<FetchAppStateResult> {
  const maxRetries = options.maxRetries ?? 3;
  const delay = options.delay ?? createDefaultDelay();

  return fetchAppStateWithRetry(1, maxRetries, delay);
}

export function buildMicrobranding(oidcContext?: OidcContext): Microbranding {
  const isEmbedded = /AtlassianMobileApp|HipChat/.test(navigator.userAgent);
  const isMobileApp = isEmbedded || isMobileOidc(oidcContext) || false;
  return {
    isEmbedded,
    isAnyMobile: screenSize() === 'mobile' || isMobileApp,
    isMobileApp,
    screenSize: screenSize(),
    oidcContext: oidcContext,
  };
}

export async function buildInitialState(
  preloaded: PreloadedAppState | null
): Promise<InitialState> {
  const microbranding = buildMicrobranding(preloaded?.oidcContext);

  // The AWS WAF config is served same-origin by the edge (not by `/frontend/state`), so it is
  // fetched separately and hydrated into its own `awsCaptcha` slice rather than into `appConfig`.
  const awsCaptcha: AwsCaptchaState = {
    ...awsCaptchaDefaultState,
    config: await fetchAwsWafConfig(),
  };

  if (!preloaded) {
    setAppStateInitializationContext({ source: 'fetched' });
    addBreadcrumb({ message: 'AppState: fetching from API' });
    const result = await fetchAppState();

    // Handle errors - throw to let index.tsx catch and render error page
    if (result.status !== 'success') {
      throw new Error(result.message);
    }

    const fetched = result.data;

    return {
      initialStoreState: {
        csrfToken: fetched.csrfToken,
        user: undefined,
        login: undefined,
        logout: undefined,
        appConfig: fetched.appConfig,
        awsCaptcha,
        locale: fetched.locale,
        continueAs: undefined,
        cancelDeletion: undefined,
        selectAccount: undefined,
        microbranding: buildMicrobranding(fetched.oidcContext),
        createAccount: undefined,
        context: {
          tenantCloudId: fetched.tenantCloudId,
          userId: undefined,
        },
        migrateConfirmation: undefined,
        welcome: undefined,
        socialLoginRecovery: undefined,
        verifyEmail: undefined,
        verifyEmailSent: undefined,
        changeEmail: undefined,
        changePassword: undefined,
        welcomeSent: undefined,
        serverError: undefined,
        cobranding: fetched.cobranding,
        stepUp: undefined,
        stepUpSso: undefined,
        verifyEmailOtp: undefined,
        loginConsent: undefined,
        orgLoginConfig: undefined,
      },
      cspNonce: fetched.cspNonce || '',
    };
  }

  setAppStateInitializationContext({ source: 'preloaded' });
  addBreadcrumb({ message: 'AppState: using preloaded value' });

  const loginState = preloaded.login && {
    ...defaultState,
    ...preloaded.login,
  };

  return {
    initialStoreState: {
      csrfToken: preloaded.csrfToken,
      hashedCsrfToken: preloaded.hashedCsrfToken,
      user: preloaded.user,
      login: loginState,
      logout: preloaded.logout,
      appConfig: preloaded.appConfig,
      awsCaptcha,
      locale: preloaded.locale,
      continueAs: preloaded.continueAs,
      cancelDeletion: preloaded.cancelDeletion,
      selectAccount: preloaded.selectAccount,
      microbranding,
      createAccount: preloaded.createAccount,
      context: {
        tenantCloudId: preloaded.tenantCloudId,
        userId: preloaded.userId,
      },
      migrateConfirmation: preloaded.migrateConfirmation,
      signup: {
        ...signupDefaultState,
        ...preloaded.signup,
      },
      welcome: {
        ...welcomeDefaultState,
        ...preloaded.welcome,
      },
      socialLoginRecovery: preloaded.socialLoginRecovery,
      verifyEmail: preloaded.verifyEmail,
      verifyEmailSent: preloaded.verifyEmailSent,
      changeEmail: preloaded.changeEmail,
      changePassword: {
        ...changePasswordDefaultState,
        ...preloaded.changePassword,
      },
      welcomeSent: preloaded.welcomeSent,
      serverError: preloaded.error,
      cobranding: preloaded.cobranding,
      multiFactor: {
        ...multiFactorDefaultState,
        ...preloaded.multiFactor,
      },
      mfaEnrollment: {
        ...mfaEnrolmentDefaultState,
        ...preloaded.enrollment,
      },
      stepUp: preloaded.stepUp,
      stepUpSso: preloaded.stepUpSso,
      verifyEmailOtp: {
        ...VerifyEmailOtpDefaultState,
        ...preloaded.verifyEmailOtp,
      },
      loginConsent: {
        ...LoginConsentDefaultState,
        ...preloaded.loginConsent,
      },
      orgLoginConfig: preloaded.orgLoginConfig,
      mfaPromote: {
        ...mfaPromoteDefaultState,
        ...preloaded.mfaPromote,
      },
    },
    cspNonce: preloaded.cspNonce,
  };
}

export function createAnalyticsClient(initialState: InitialState): AnalyticsClientImpl {
  const { microbranding, context } = initialState.initialStoreState;
  const locale = undefined;

  return new AnalyticsClientImpl({
    microbranding,
    locale,
    tenantCloudId: context.tenantCloudId,
    userId: context.userId,
    client: undefined,
  });
}

export function startupAnalyticEvent(analyticsClient: AnalyticsClient) {
  analyticsClient.operationalEvent({
    page: 'unknown',
    action: 'loadPage',
    subject: 'sli',
  });
}

export function pushGlobals(appConfig: AppConfig | undefined, cspNonce: string) {
  window.segmentIoKey = appConfig && appConfig.segmentIoKey;
  window.cspNonce = cspNonce;
}
